// New file in the playground: an endpoint under src/api/ (folder = route), a helper under src/services/, or any other file.
import { useEffect, useState } from 'react'
import { Modal } from '@/kit/ui'
import { apiFilePath, apiRootOf, isValidPath } from '@/lib/endpoints'
import { isApiPath, routeOf } from '@/lib/manifest'

export type NewFileKind = 'endpoint' | 'helper' | 'empty'

const KINDS: { id: NewFileKind; label: string; help: string }[] = [
  { id: 'endpoint', label: 'Endpoint (under src/api/)', help: 'One REST endpoint: module.exports = defineApi({ outcomes, handler }). Its route is its place under src/api/: src/api/v2/get-offer.js → POST /v2/get-offer.' },
  { id: 'helper', label: 'Helper code (src/services/ or anywhere else)', help: 'The lender client, formatting, anything endpoints require(). Not an endpoint.' },
  { id: 'empty', label: 'Empty file', help: 'A README, notes, a JSON sample: anything that is not code.' },
]

export function NewFileDialog({ isOpen, folder, files, exists, onClose, onCreate }: {
  isOpen: boolean
  folder: string
  /** the working copy, to know whether this batch uses src/api or the older api/ */
  files: { path: string }[]
  exists: (path: string) => boolean
  onClose: () => void
  onCreate: (path: string, kind: NewFileKind, title: string) => void
}) {
  const apiRoot = apiRootOf(files)
  const [path, setPath] = useState('')
  const [kind, setKind] = useState<NewFileKind>('endpoint')
  const [title, setTitle] = useState('')
  useEffect(() => {
    if (!isOpen) return
    const inApi = isApiPath(folder + '/')
    setPath(folder ? `${folder}/` : `${apiRoot}/`)
    setKind(inApi || !folder ? 'endpoint' : folder.startsWith('src/services') || folder.startsWith('lib') ? 'helper' : 'empty')
    setTitle('')
  }, [isOpen, folder, apiRoot])

  const full = kind === 'endpoint' && !isApiPath(path) ? (apiRoot === 'api' ? `api/${path.replace(/^\/+/, '')}` : apiFilePath(path)) : path
  const fileName = full.split('/').pop() ?? ''
  const route = kind === 'endpoint' && full.endsWith('.js') ? routeOf(full) : null
  const error = !fileName ? 'Type a file name' : !isValidPath(full) ? 'Letters, numbers, - _ . and / only' : exists(full) ? `${full} already exists` : kind !== 'empty' && !full.endsWith('.js') ? 'A code file ends with .js' : kind === 'endpoint' && route === '/' ? `Give the endpoint a name: ${apiRoot}/v1/<name>.js` : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New file"
      footer={
        <div className="flex justify-end gap-2">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!!error} onClick={() => onCreate(full, kind, title.trim() || fileName.replace(/\.js$/, ''))} data-testid="new-file-create">Create</button>
        </div>
      }
    >
      <label className="label" htmlFor="nf-path">Path (folders are made as needed)</label>
      <input id="nf-path" className="input mono" value={path} onChange={(e) => setPath(e.target.value)} placeholder={`${apiRoot}/v2/soft-offer.js`} autoFocus />
      {kind === 'endpoint' && route && route !== '/' && <p className="mt-1 text-2xs text-muted">Route: <code>POST {route}</code>{full !== path ? ` (file ${full})` : ''}</p>}
      <fieldset className="mt-3 space-y-1.5">
        <legend className="label">What it holds</legend>
        {KINDS.map((k) => (
          <label key={k.id} className="flex cursor-pointer gap-2 rounded border border-slate-200 p-2 text-xs dark:border-slate-700">
            <input type="radio" name="nf-kind" checked={kind === k.id} onChange={() => setKind(k.id)} className="mt-0.5" />
            <span><b>{k.label}</b><span className="block text-2xs text-muted">{k.help}</span></span>
          </label>
        ))}
      </fieldset>
      {kind === 'endpoint' && (
        <div className="mt-2">
          <label className="label" htmlFor="nf-title">Title (shown in lists)</label>
          <input id="nf-title" className="input" value={title} placeholder="Soft offer" onChange={(e) => setTitle(e.target.value)} />
        </div>
      )}
      {error && path && <p className="field-error mt-2">{error}</p>}
      <p className="mt-3 text-2xs text-muted">A new endpoint gets one test event “Reply 200 → ok”. It is in the sandbox's working copy: Save makes the commit.</p>
    </Modal>
  )
}
